import { AnimatedImageDto } from "@repo/api/animated-image";
import { CompositionDto } from "@repo/api/editor";
import { getAnimatedImageConfig } from "@repo/api/utils/composition";
import { getMediaSrc } from "./media.ts";

const prefetchedSrcs = new Set<string>();

export function getAnimatedImageSrcs({
  composition,
  animatedImages,
}: {
  composition: CompositionDto;
  animatedImages: Record<string, AnimatedImageDto>;
}) {
  const srcs = composition.mainSceneTimeline.flatMap((item) => {
    const animatedImage = item.animatedImageExternalId
      ? animatedImages[item.animatedImageExternalId]
      : undefined;

    if (!animatedImage || animatedImage.status !== "COMPLETED") {
      return [];
    }

    const src = getAnimatedImageConfig(item).reverse
      ? animatedImage.backwardSrc
      : animatedImage.forwardSrc;

    return [getMediaSrc(src)];
  });

  return [...new Set(srcs)];
}

/**
 * Downloads the videos one at a time into the browser's HTTP cache, discarding
 * the bytes so nothing is kept in memory.
 */
export async function prefetchVideos({
  srcs,
  signal,
}: {
  srcs: string[];
  signal: AbortSignal;
}) {
  for (const src of srcs) {
    if (prefetchedSrcs.has(src)) {
      continue;
    }

    try {
      const response = await fetch(src, { signal });
      const reader = response.body!.getReader();
      let result;
      do {
        result = await reader.read();
      } while (!result.done);

      if (response.ok) {
        prefetchedSrcs.add(src);
      }
    } catch {
      if (signal.aborted) {
        return;
      }
    }
  }
}
